// Client-side cache of table/view names per (connectionId, database) for
// MSSQL. SSTS (the SQL Tools Service LSP) only returns tables through its
// dot-qualified completion path — an unqualified `SELECT * FROM SalesOrder`
// never surfaces `Sales.SalesOrderDetail` because SSTS is schema-first by
// design. This cache feeds a renderer-side supplement that makes the
// MSSQL completion experience match PG/MySQL/Oracle: typing a bare prefix
// after FROM/JOIN/UPDATE/INTO surfaces cross-schema tables.
//
// TIERED LOAD (SLICES.md § S7 items 1–4, 7 — from a live large-database
// repro where the pill sat on "Initializing", the renderer froze for seconds
// and a toast announced a 50,000-row clip):
//
//   phase 1 — table list + server info ONLY. Published to the schema bus the
//             moment it lands, which flips the IntelliSense pill to ready and
//             makes table-name completion work. `ensureMssqlTables` resolves
//             here.
//   phase 2 — columns / FKs / CHECKs / indexes / routines / sequences, fetched
//             afterwards, merged page by page into the SAME snapshot object
//             and re-published as they arrive (`whenMssqlSnapshotComplete`
//             awaits it). Per-table lazy column fetch (`mssqlColumnCache.ts`)
//             remains the fallback for a table phase 2 has not reached yet.
//
// Every phase-2 fetch is paged (`introspectionPaging.ts`, 5,000 rows per
// page), gated to 2 concurrent queries per connection
// (`introspectionSemaphore.ts`) so a user query issued during the load is not
// stuck behind the catalog scans, and merged incrementally
// (`mssqlSnapshotMerge.ts`) with an event-loop yield between pages so no
// main-thread task exceeds 50 ms.
//
// The TTL is deliberately long (6 hours): the snapshot is invalidated by DDL
// (`ddlInvalidator.ts` → `dropMssqlTables` + `invalidateSchemaBus`), by an
// explicit refresh (`forceRefresh: true`) and by reconnect — not by a timer.
// The old 60-second TTL re-ran the whole fan-out about once a minute, which is
// what turned a slow first load into a recurring freeze.

import type { FkEdge, IndexMeta, RoutineMeta, SequenceMeta } from '@/core/engines/intellisenseTypes';
import { publish as publishSchemaBus } from '@/features/intellisense/schemaBus/schemaBus';
import { projectMssqlSnapshot } from '@/features/intellisense/schemaBus/adapters/mssqlAdapter';

import { resolveMssqlServerYear } from './mssqlServerVersion';
import {
  fetchMssqlForeignKeyPages,
  fetchMssqlRoutinePages,
  fetchMssqlSequencePages,
} from './mssqlSchemaIntrospection';
import {
  fetchMssqlColumnPages,
  fetchMssqlCheckConstraintPages,
  type MssqlColumnIntrospection,
  type MssqlCheckIntrospection,
} from './mssqlColumnIntrospection';
import {
  fetchMssqlIndexPages,
  type MssqlIndexIntrospection,
} from './mssqlIndexIntrospection';
import { INTROSPECTION_PAGE_ROWS, isCompleteOutcome, yieldToEventLoop } from './introspectionPaging';
// TOTAL row budget for the table list across all pages (S7 item 4).
import { TABLE_ROW_BUDGET } from './introspectionBudgets';
import { beginIntrospectionLoad } from './introspectionTruncation';
import { SNAPSHOT_STALE_AFTER_MS } from './snapshotLifetime';
import {
  createLoadFailures,
  isEntryFresh,
  markLoadOutcome,
  publishDegraded,
  recordLoadFailure,
  reportPartialSnapshot,
  whenEntryColumnsLoaded,
  type LoadFailures,
} from './tieredSnapshotCache';
import { fetchMssqlPagedArtefact } from './mssqlIntrospectionQuery';
import {
  applyCheckPage,
  applyColumnPage,
  applyForeignKeyPage,
  applyIndexPage,
  applyRoutinePage,
  applySequencePage,
  carryOverIntrospection,
  createMssqlLiveSnapshot,
  type MssqlLiveSnapshot,
  reconcileChecks,
  reconcileIndexes,
} from './mssqlSnapshotMerge';

export type MssqlTableColumnEntry = {
  readonly name: string;
  readonly dataType: string;
  readonly nullable: boolean;
  readonly isPrimaryKey: boolean;
  /** `sys.columns.is_identity` — see `ColumnMeta.isIdentity`. Undefined when
   *  the column fetch predates the flag (never for a live refresh). */
  readonly isIdentity?: boolean;
  /** `sys.columns.is_computed` — see `ColumnMeta.isComputed`. */
  readonly isComputed?: boolean;
  readonly ordinal: number;
};

export type MssqlTableEntry = {
  schema: string;
  name: string;
  type: 'table' | 'view';
  /** Populated by phase 2; absent until its first column page for this table. */
  columns?: readonly MssqlTableColumnEntry[];
  /** `sys.check_constraints.definition` strings, one per CHECK on this table. */
  checkConstraints?: readonly string[];
  /** Indexes on the table (`sys.indexes` + `sys.index_columns`) — the
   *  index-NAME vocabulary of `DROP INDEX ▮ ON t`. Absent for views and for
   *  snapshots taken before the fetch existed. */
  indexes?: readonly IndexMeta[];
};

/**
 * How much of the snapshot has been filled in. `'tables'` is a complete,
 * usable snapshot for table-name completion whose per-table payload is still
 * loading; `'complete'` means phase 2 finished (successfully or with a
 * degraded artefact — see `feature:schema_snapshot_partial`).
 */
export type MssqlSnapshotPhase = 'tables' | 'complete';

export type MssqlTableSnapshot = {
  connectionId: string;
  database: string;
  tables: MssqlTableEntry[];
  /** FK edges for the database. Filled in by phase 2. */
  foreignKeys: readonly FkEdge[];
  /** User routines (procs + scalar/table-valued functions) with parameter
   *  signatures. Filled in by phase 2. */
  routines?: readonly RoutineMeta[];
  /** User sequences (`sys.sequences`) — `NEXT VALUE FOR` targets. Filled in
   *  by phase 2. */
  sequences?: readonly SequenceMeta[];
  /**
   * SQL Server marketing **year** (e.g. `'2022'`, `'2017'`) derived from
   * `SERVERPROPERTY('ProductMajorVersion')` via `mssqlMajorVersionToYear`.
   * NOT a `@@VERSION` banner or a `ProductVersion` build string — the value
   * is the year token so it is directly comparable to the year-format
   * `versionMetadata.addedIn` (`'2016'|'2017'|'2019'|'2022'`). Empty when the
   * probe failed or the major was unrecognized (→ inert gating). Drives
   * IntelliSense version gating (INT-1) — the bus projector maps this onto
   * `SchemaSnapshot.serverVersion`.
   */
  serverVersion?: string;
  /**
   * Per-database `compatibility_level` (e.g. 90, 130, 160) read from the live
   * connection's `sys.databases`. Drives compat-level version gating (INT-1)
   * in production — replaces the test-only override as the real source.
   * `null` when the probe failed.
   */
  mssqlCompatLevel?: number | null;
  /**
   * `SCHEMA_NAME()` — the default schema of the database user the login maps
   * to in this database (`dbo` for a sysadmin or a user without one). SQL
   * Server resolves an unqualified object name there first, then in `dbo`;
   * the resolver projection turns it into `SchemaSnapshot.defaultSchemas`.
   * Absent when the probe failed.
   */
  defaultSchema?: string;
  /**
   * See `MssqlSnapshotPhase`. Always set by `ensureMssqlTables`; optional so a
   * hand-built fixture (or a caller that assembles a snapshot itself) does not
   * have to claim a phase it never went through.
   */
  phase?: MssqlSnapshotPhase;
  builtAt: number;
};

type CacheEntry = {
  snapshot?: MssqlTableSnapshot;
  lastError?: string;
  /** See `TieredCacheEntry.degradedAt` — a load that ended with a failed artefact is retried soon. */
  degradedAt?: number;
  /** Phase-1 dedup — resolves as soon as the tables-only snapshot publishes. */
  refreshPromise?: Promise<MssqlTableSnapshot>;
  /** Phase-2 completion for the CURRENT generation. Never rejects. */
  phase2?: Promise<void>;
  /** See `TieredCacheEntry.columnsLoaded` — the column step of the CURRENT generation. */
  columnsLoaded?: Promise<void>;
  /** Bumped per refresh; a superseded phase 2 stops merging. */
  generation: number;
  lastPublishAt: number;
};

const cache = new Map<string, CacheEntry>();

/**
 * Snapshot lifetime — the shared cross-engine backstop
 * (`snapshotLifetime.ts`), not a refresh cadence: the snapshot is invalidated
 * by DDL, explicit refresh and reconnect instead, see the header. Re-exported
 * under the cache's historical name because `mssqlLanguageServiceProvider.ts`
 * passes the cache's own TTL back in rather than hand-duplicating a number.
 */
export const DEFAULT_TTL_MS = SNAPSHOT_STALE_AFTER_MS;

/**
 * How often the bus may be re-published while phase 2 streams in. Every page
 * merges immediately, but publishing each one would wake every bus subscriber
 * (editors, VQE contexts, the pill) dozens of times per second on a large
 * database. The final publish of each artefact is always forced, so the last
 * state is never left unpublished.
 */
const REPUBLISH_MIN_INTERVAL_MS = 150;

function keyFor(connectionId: string, database: string): string {
  return `${connectionId}|${database}`;
}

function buildTablesPageSql(offsetFetch: string): string {
  return `SELECT TABLE_SCHEMA, TABLE_NAME, TABLE_TYPE
FROM INFORMATION_SCHEMA.TABLES
WHERE TABLE_TYPE IN ('BASE TABLE', 'VIEW')
ORDER BY TABLE_SCHEMA, TABLE_NAME
${offsetFetch}`;
}

/** Row shape → `MssqlTableEntry`; exported for the paging unit tests. */
export function aggregateTableRows(
  rows: readonly Record<string, unknown>[],
): MssqlTableEntry[] {
  const out: MssqlTableEntry[] = [];
  for (const row of rows) {
    const schema = String(row.TABLE_SCHEMA ?? '').trim();
    const name = String(row.TABLE_NAME ?? '').trim();
    if (!schema || !name) continue;
    const type: MssqlTableEntry['type'] =
      String(row.TABLE_TYPE ?? '').toUpperCase() === 'VIEW' ? 'view' : 'table';
    out.push({ schema, name, type });
  }
  return out;
}

async function fetchTables(connectionId: string, database: string): Promise<MssqlTableEntry[]> {
  if (!window.electronAPI?.executeQueryV2) return [];
  const out: MssqlTableEntry[] = [];
  await fetchMssqlPagedArtefact<MssqlTableEntry>({
    connectionId,
    database,
    label: 'tables',
    artefact: 'tables',
    sqlForPage: buildTablesPageSql,
    aggregate: aggregateTableRows,
    sink: (page) => {
      out.push(...page);
    },
    totalBudget: TABLE_ROW_BUDGET,
  });
  return out;
}

type MssqlServerInfo = { serverVersion: string; compatLevel: number | null; defaultSchema?: string };

const NO_SERVER_INFO: MssqlServerInfo = { serverVersion: '', compatLevel: null };

/**
 * Probe the server version + per-database `compatibility_level` once per
 * snapshot refresh so IntelliSense version gating (INT-1) compares against
 * the live connection, plus the database user's default schema
 * (`SCHEMA_NAME()`) that unqualified names resolve through. All come back in
 * one round-trip, in phase 1 — the gating hint must be right on the first
 * completion, and the probe is O(1).
 *
 * `SERVERPROPERTY('ProductMajorVersion')` returns the integer major
 * (11/12/…/16/17, available SQL Server 2012+). We carry `ProductVersion`
 * alongside it purely as a fallback so a pre-2012 server (no
 * `ProductMajorVersion`) can still resolve via its leading integer. The
 * raw major is mapped to the SQL Server marketing **year** (`'2022'`, …)
 * via `resolveMssqlServerYear` so it is comparable to the year-format
 * `versionMetadata.addedIn`. On failure the fields stay empty/null and the
 * projector maps them to "unknown" (no gating) — never blocks the snapshot.
 */
async function fetchMssqlServerInfo(
  connectionId: string,
  database: string,
): Promise<MssqlServerInfo> {
  if (!window.electronAPI?.executeQueryV2) return NO_SERVER_INFO;
  const sql = `SELECT CAST(SERVERPROPERTY('ProductMajorVersion') AS nvarchar(16)) AS product_major,
  CAST(SERVERPROPERTY('ProductVersion') AS nvarchar(128)) AS product_version,
  (SELECT compatibility_level FROM sys.databases WHERE name = DB_NAME()) AS compat_level,
  SCHEMA_NAME() AS default_schema`;
  const result = await window.electronAPI.executeQueryV2({
    connectionId,
    database,
    sql,
    executionId: `mssql-ls-serverinfo-${connectionId}-${database}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  });
  if (!result.success) return NO_SERVER_INFO;
  const row = result.resultSets?.[0]?.rows?.[0];
  if (!row) return NO_SERVER_INFO;
  const rawCompat = row.compat_level;
  const compatLevel =
    typeof rawCompat === 'number'
      ? rawCompat
      : typeof rawCompat === 'string' && /^\d+$/.test(rawCompat)
        ? Number.parseInt(rawCompat, 10)
        : null;
  const defaultSchema = typeof row.default_schema === 'string' ? row.default_schema.trim() : '';
  return {
    serverVersion: resolveMssqlServerYear(row.product_major, row.product_version),
    compatLevel,
    ...(defaultSchema ? { defaultSchema } : {}),
  };
}

/**
 * Pure helper: merge bulk-mode column + CHECK introspection into the table list
 * keyed by `(schema|name)`. Exported for testability — the per-(schema, name)
 * grouping and casing rules belong to one place rather than scattered across
 * the cache + projector layers. The tiered load merges page by page through
 * `mssqlSnapshotMerge.ts` instead; this stays the one-shot form used by
 * callers that already hold every artefact (and by its own unit tests).
 */
export function mergeTableIntrospection(
  tables: readonly MssqlTableEntry[],
  columns: readonly MssqlColumnIntrospection[],
  checks: readonly MssqlCheckIntrospection[],
  indexes: readonly MssqlIndexIntrospection[] = [],
): MssqlTableEntry[] {
  const colsByTable = new Map<string, MssqlTableColumnEntry[]>();
  for (const c of columns) {
    const key = `${c.schema.toLowerCase()}|${c.table.toLowerCase()}`;
    const list = colsByTable.get(key) ?? [];
    list.push({
      name: c.name,
      dataType: c.dataType,
      nullable: c.nullable,
      isPrimaryKey: c.isPrimaryKey,
      isIdentity: c.isIdentity,
      isComputed: c.isComputed,
      ordinal: c.ordinal,
    });
    colsByTable.set(key, list);
  }
  const checksByTable = new Map<string, string[]>();
  for (const c of checks) {
    const key = `${c.schema.toLowerCase()}|${c.table.toLowerCase()}`;
    const list = checksByTable.get(key) ?? [];
    list.push(c.definition);
    checksByTable.set(key, list);
  }
  const indexesByTable = new Map<string, IndexMeta[]>();
  for (const i of indexes) {
    const key = `${i.schema.toLowerCase()}|${i.table.toLowerCase()}`;
    const list = indexesByTable.get(key) ?? [];
    list.push(i.index);
    indexesByTable.set(key, list);
  }
  return tables.map((t) => {
    const key = `${t.schema.toLowerCase()}|${t.name.toLowerCase()}`;
    const cols = colsByTable.get(key);
    const tableChecks = checksByTable.get(key);
    const tableIndexes = indexesByTable.get(key);
    if (!cols && !tableChecks && !tableIndexes) return t;
    return {
      ...t,
      ...(cols ? { columns: cols } : {}),
      ...(tableChecks ? { checkConstraints: tableChecks } : {}),
      ...(tableIndexes ? { indexes: tableIndexes } : {}),
    };
  });
}

// E2E seam — mirrors `__jamSqlSchemaBusPeek__`: lets the coverage harness
// force a snapshot refresh + bus publish after it has invalidated post-DDL
// (see `__jamSqlInvalidateAfterDdl__` in ddlInvalidator.ts), so the NEXT
// checkpoint resolves against a warm, fresh snapshot instead of racing the
// fire-and-forget re-ensure. Zero-cost in production. Resolves only once
// phase 2 has finished, so a column checkpoint never reads a tables-only
// snapshot.
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__jamSqlEnsureMssqlTables__ = (
    connectionId: string,
    database: string,
  ): Promise<unknown> =>
    ensureMssqlTablesComplete({ connectionId, database, forceRefresh: true });
}

export function getCachedMssqlTables(
  connectionId: string,
  database: string,
): MssqlTableSnapshot | undefined {
  return cache.get(keyFor(connectionId, database))?.snapshot;
}

export function dropMssqlTables(connectionId: string): void {
  const prefix = `${connectionId}|`;
  for (const key of Array.from(cache.keys())) {
    if (key.startsWith(prefix)) {
      // Bump the generation before dropping so an in-flight phase 2 stops
      // merging into a snapshot nobody can read any more.
      const entry = cache.get(key);
      if (entry) entry.generation += 1;
      cache.delete(key);
    }
  }
}

// One-shot prewarm of the T-SQL procedural grammar (the vendored ANTLR
// lexer + parser, a ~9 MB lazily-imported chunk). Without this, the FIRST
// completion request that needs `@local` resolution pays the chunk load +
// module compile inline — under load that request resolves after Monaco has
// already shown the list, so the locals silently never appear (the recurring
// 33.§4-class flake). Kicked from `ensureMssqlTables` because this is the
// mssql warm-on-connect site: the parse of a trivial body warms the chunk,
// the parser construction, and the ATN before any real keystroke needs it.
let tsqlGrammarPrewarmKicked = false;
function prewarmTsqlProceduralGrammar(): void {
  if (tsqlGrammarPrewarmKicked) return;
  tsqlGrammarPrewarmKicked = true;
  void import('@/features/sql-grammar-tsql/parse')
    .then((m) => m.parseProceduralBody('DECLARE @warm INT;', { engine: 'mssql' }))
    .catch(() => {
      tsqlGrammarPrewarmKicked = false;
    });
}

function publishLive(entry: CacheEntry, live: MssqlLiveSnapshot, force: boolean): void {
  const now = Date.now();
  if (!force && now - entry.lastPublishAt < REPUBLISH_MIN_INTERVAL_MS) return;
  entry.lastPublishAt = now;
  // Shallow copy so subscribers that compare snapshot identity see a change;
  // the tables / FK / routine arrays are shared with the live snapshot on
  // purpose — a consumer holding an older publish keeps reading fresh data.
  publishSchemaBus({ ...live.projected });
}

/**
 * Phase 1 — the table list plus the version probe. Publishes the moment it
 * lands and kicks phase 2 without awaiting it.
 */
async function runPhase1(
  entry: CacheEntry,
  options: { connectionId: string; database: string },
): Promise<MssqlTableSnapshot> {
  const generation = entry.generation + 1;
  entry.generation = generation;
  const failures = createLoadFailures();
  // The record describes THIS load, not the previous one (R3).
  beginIntrospectionLoad(options.connectionId, options.database);

  let relationsFailed = false;
  const [tables, serverInfo] = await Promise.all([
    fetchTables(options.connectionId, options.database).catch((error: unknown) => {
      relationsFailed = true;
      recordLoadFailure(failures, 'relations', error);
      return [] as MssqlTableEntry[];
    }),
    fetchMssqlServerInfo(options.connectionId, options.database).catch((error: unknown) => {
      recordLoadFailure(failures, 'serverinfo', error);
      return NO_SERVER_INFO;
    }),
  ]);

  // The cache is keyed by (connection, database), so `previous` is always the
  // same scope here — no cross-database carry-over is possible.
  const previous = entry.snapshot;
  const snapshot: MssqlTableSnapshot = {
    connectionId: options.connectionId,
    database: options.database,
    // Stale-while-revalidate: keep the previous refresh's columns/CHECKs/
    // indexes on tables that still exist until phase 2 replaces them.
    tables: carryOverIntrospection(previous?.tables, tables),
    foreignKeys: previous?.foreignKeys ?? [],
    routines: previous?.routines ?? [],
    sequences: previous?.sequences ?? [],
    serverVersion: serverInfo.serverVersion,
    mssqlCompatLevel: serverInfo.compatLevel,
    ...(serverInfo.defaultSchema ? { defaultSchema: serverInfo.defaultSchema } : {}),
    phase: 'tables',
    builtAt: Date.now(),
  };

  if (relationsFailed) {
    // Never cache a relation-less snapshot as a clean, complete one
    // (release review 1.5.0, F3) — see `publishDegraded`.
    return publishDegraded(entry, generation, {
      previous,
      built: snapshot,
      failures,
      engine: 'mssql',
      project: projectMssqlSnapshot,
    });
  }

  const live = await createMssqlLiveSnapshot(snapshot);
  // A drop / newer refresh landed while we were projecting — discard.
  if (entry.generation !== generation) return snapshot;

  entry.snapshot = snapshot;
  publishLive(entry, live, true);
  entry.phase2 = runPhase2(entry, live, generation, failures);
  return snapshot;
}

/**
 * Phase 2 — every per-table and per-database artefact, paged, merged
 * incrementally and re-published as pages land. Never rejects: an artefact
 * that fails degrades to whatever pages already merged and is named in the
 * `feature:schema_snapshot_partial` event.
 */
async function runPhase2(
  entry: CacheEntry,
  live: MssqlLiveSnapshot,
  generation: number,
  failures: LoadFailures,
): Promise<void> {
  const { connectionId, database } = live.native;
  const superseded = (): boolean => entry.generation !== generation;

  const onPage = (merge: () => void): void => {
    if (superseded()) return;
    merge();
    publishLive(entry, live, false);
  };

  const step = async (name: string, run: () => Promise<void>): Promise<void> => {
    if (superseded()) return;
    try {
      await run();
    } catch (error) {
      recordLoadFailure(failures, name, error);
    }
    if (!superseded()) publishLive(entry, live, false);
  };

  const abort = { shouldAbort: superseded };
  // Exposed as `columnsLoaded` (set before this function first yields) so the
  // Visual Query Editor can start editing without waiting for the other steps.
  const columns = step('columns', () =>
    fetchMssqlColumnPages(
      connectionId,
      database,
      (page) => onPage(() => applyColumnPage(live, page)),
      abort,
    ),
  );
  entry.columnsLoaded = columns;
  await Promise.all([
    columns,
    // Only a COMPLETE read may clear carried-over CHECKs / indexes on tables
    // it never touched; a capped or aborted one proves nothing about them.
    step('checks', async () => {
      const outcome = await fetchMssqlCheckConstraintPages(
        connectionId,
        database,
        (page) => onPage(() => applyCheckPage(live, page)),
        abort,
      );
      if (isCompleteOutcome(outcome)) onPage(() => reconcileChecks(live));
    }),
    step('indexes', async () => {
      const outcome = await fetchMssqlIndexPages(
        connectionId,
        database,
        (page) => onPage(() => applyIndexPage(live, page)),
        abort,
      );
      if (isCompleteOutcome(outcome)) onPage(() => reconcileIndexes(live));
    }),
    step('fk', () =>
      fetchMssqlForeignKeyPages(
        connectionId,
        database,
        (page) => onPage(() => applyForeignKeyPage(live, page)),
        abort,
      ),
    ),
    step('routines', () =>
      fetchMssqlRoutinePages(
        connectionId,
        database,
        (page) => onPage(() => applyRoutinePage(live, page)),
        abort,
      ),
    ),
    step('sequences', () =>
      fetchMssqlSequencePages(
        connectionId,
        database,
        (page) => onPage(() => applySequencePage(live, page)),
        abort,
      ),
    ),
  ]);

  if (superseded()) return;
  live.native.phase = 'complete';
  markLoadOutcome(entry, failures.missing, { connectionId, database });
  publishLive(entry, live, true);
  reportPartialSnapshot('mssql', failures);
}

export async function ensureMssqlTables(options: {
  connectionId: string;
  database: string;
  ttlMs?: number;
  forceRefresh?: boolean;
}): Promise<MssqlTableSnapshot> {
  prewarmTsqlProceduralGrammar();
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const key = keyFor(options.connectionId, options.database);
  const entry = cache.get(key) ?? { generation: 0, lastPublishAt: 0 };
  cache.set(key, entry);

  if (!options.forceRefresh && isEntryFresh(entry, ttlMs)) {
    return entry.snapshot as MssqlTableSnapshot;
  }

  if (entry.refreshPromise) {
    return entry.refreshPromise;
  }

  entry.refreshPromise = runPhase1(entry, options).finally(() => {
    entry.refreshPromise = undefined;
  });

  return entry.refreshPromise;
}

/**
 * Resolve once the snapshot for `(connectionId, database)` is fully loaded —
 * phase 1 published AND phase 2 finished merging. Returns immediately when
 * nothing is in flight (including when no snapshot exists at all: callers who
 * want one call `ensureMssqlTables` first, or use
 * `ensureMssqlTablesComplete`). Never rejects — a failed refresh resolves,
 * because "complete" here means "no longer loading", not "succeeded".
 */
export async function whenMssqlSnapshotComplete(
  connectionId: string,
  database: string,
): Promise<void> {
  const entry = cache.get(keyFor(connectionId, database));
  if (!entry) return;
  // A refresh started while we awaited restarts the wait; bounded so a
  // pathological refresh loop cannot hang a caller forever.
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const refresh = entry.refreshPromise;
    if (refresh) await refresh.catch(() => undefined);
    const phase2 = entry.phase2;
    if (phase2) await phase2.catch(() => undefined);
    if (!entry.refreshPromise) return;
  }
}

/**
 * Resolve once the snapshot's columns have loaded (phase 1 + the phase-2
 * column step), without waiting for CHECK clauses, indexes, FK edges,
 * routines or sequences — see `whenEntryColumnsLoaded`.
 */
export async function whenMssqlColumnsLoaded(connectionId: string, database: string): Promise<void> {
  await whenEntryColumnsLoaded(cache.get(keyFor(connectionId, database)));
}

/** `ensureMssqlTables` + `whenMssqlColumnsLoaded` — the Visual Query Editor's
 *  live schema pull: editable once tables + columns are in; the later
 *  artefacts arrive through the schema bus. */
export async function ensureMssqlTablesWithColumns(options: {
  connectionId: string;
  database: string;
  ttlMs?: number;
  forceRefresh?: boolean;
}): Promise<MssqlTableSnapshot> {
  const snapshot = await ensureMssqlTables(options);
  await whenMssqlColumnsLoaded(options.connectionId, options.database);
  return getCachedMssqlTables(options.connectionId, options.database) ?? snapshot;
}

/** `ensureMssqlTables` + `whenMssqlSnapshotComplete` — for callers that need
 *  the WHOLE snapshot (the coverage harness's post-DDL hook, the pill's
 *  "Reload schema" action). */
export async function ensureMssqlTablesComplete(options: {
  connectionId: string;
  database: string;
  ttlMs?: number;
  forceRefresh?: boolean;
}): Promise<MssqlTableSnapshot> {
  const snapshot = await ensureMssqlTables(options);
  await whenMssqlSnapshotComplete(options.connectionId, options.database);
  return getCachedMssqlTables(options.connectionId, options.database) ?? snapshot;
}

/**
 * Test seam for the long-task perf gate: build a synthetic snapshot of
 * `tableCount` tables × `columnsPerTable` columns and push it through the REAL
 * chunked path — the same `createMssqlLiveSnapshot` projection, the same
 * `applyColumnPage` merge in `INTROSPECTION_PAGE_ROWS`-sized pages, the same
 * throttled bus publishes. Rows are generated per page rather than up front so
 * the generation itself cannot be the long task the gate measures.
 */
export async function injectSyntheticMssqlSnapshotForTest(options: {
  connectionId: string;
  database: string;
  tableCount: number;
  columnsPerTable: number;
}): Promise<void> {
  const key = keyFor(options.connectionId, options.database);
  const entry = cache.get(key) ?? { generation: 0, lastPublishAt: 0 };
  cache.set(key, entry);
  const generation = entry.generation + 1;
  entry.generation = generation;

  const tables: MssqlTableEntry[] = [];
  for (let i = 0; i < options.tableCount; i += 1) {
    tables.push({ schema: 'dbo', name: `synthetic_table_${i}`, type: 'table' });
    if (tables.length % INTROSPECTION_PAGE_ROWS === 0) await yieldToEventLoop();
  }

  const snapshot: MssqlTableSnapshot = {
    connectionId: options.connectionId,
    database: options.database,
    tables,
    foreignKeys: [],
    routines: [],
    sequences: [],
    phase: 'tables',
    builtAt: Date.now(),
  };
  const live = await createMssqlLiveSnapshot(snapshot);
  entry.snapshot = snapshot;
  publishLive(entry, live, true);

  let page: MssqlColumnIntrospection[] = [];
  const flush = async (): Promise<void> => {
    if (page.length === 0) return;
    applyColumnPage(live, page);
    page = [];
    publishLive(entry, live, false);
    await yieldToEventLoop();
  };
  for (let t = 0; t < options.tableCount; t += 1) {
    for (let c = 0; c < options.columnsPerTable; c += 1) {
      page.push({
        schema: 'dbo',
        table: `synthetic_table_${t}`,
        name: `col_${c}`,
        dataType: 'nvarchar(100)',
        nullable: true,
        isPrimaryKey: c === 0,
        isIdentity: false,
        isComputed: false,
        ordinal: c + 1,
      });
      if (page.length >= INTROSPECTION_PAGE_ROWS) await flush();
    }
  }
  await flush();
  live.native.phase = 'complete';
  publishLive(entry, live, true);
}
